import type { FieldTaskEvent } from '../api/types';

/**
 * Menyusun jejak tugas dari event yang SUDAH terekam — MURNI, tanpa React dan tanpa peta.
 *
 * Yang digambar adalah TITIK KEJADIAN pada tiap transisi (Terima → Mulai → Tiba → Selesai),
 * bukan jejak perjalanan. Workforce tidak melacak lokasi terus-menerus, dan tidak bisa: ia
 * aplikasi web, dan peramban menghentikan pembacaan GPS begitu layar terkunci atau aplikasi
 * berpindah ke latar. Perbedaan itu disebut di layar, bukan disamarkan dengan garis mulus yang
 * memberi kesan kami tahu jalan yang ditempuh.
 */

export interface TrailPoint {
  readonly id: string;
  readonly sequenceNo: number;
  readonly eventType: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMeters: number | null;
  readonly occurredAtServer: string;
  readonly distanceToDestinationMeters: number | null;
}

export interface TaskTrail {
  /** Titik terpakai, URUT menurut `sequenceNo` — bukan menurut waktu perangkat. */
  readonly points: readonly TrailPoint[];
  /**
   * Event yang tidak menyumbang titik — DIHITUNG, bukan disembunyikan.
   *
   * Peta yang diam-diam membuang titik memberi tahu hal yang salah dengan percaya diri: kurir
   * yang menekan tombol di basemen tanpa sinyal terlihat seperti kurir yang tidak menekan apa pun.
   */
  readonly withoutLocation: number;
}

export interface LatLng {
  readonly latitude: number;
  readonly longitude: number;
}

export interface Bounds {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
}

/**
 * Koordinat yang layak digambar.
 *
 * `0, 0` DITOLAK meski secara teknis sah. Titik itu ada di Teluk Guinea, ribuan kilometer dari
 * wilayah kerja mana pun yang kami layani, dan dalam praktiknya ia selalu berarti "nilai gagal
 * terbaca" — bukan lokasi. Satu titik semacam itu merentangkan peta ke seluruh planet dan membuat
 * jejak yang sesungguhnya menyusut jadi satu noktah: seluruh layar rusak oleh satu baris data.
 * Ia tetap MASUK HITUNGAN `withoutLocation`, jadi pembuangannya terlihat.
 */
function usable(latitude: unknown, longitude: unknown): boolean {
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return false;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (latitude < -90 || latitude > 90) return false;
  if (longitude < -180 || longitude > 180) return false;
  return !(latitude === 0 && longitude === 0);
}

export function buildTaskTrail(events: readonly FieldTaskEvent[]): TaskTrail {
  const points: TrailPoint[] = [];
  let withoutLocation = 0;

  for (const event of events) {
    if (event === null || typeof event !== 'object') continue;
    if (!usable(event.latitude, event.longitude)) {
      withoutLocation += 1;
      continue;
    }
    points.push({
      id: event.id,
      sequenceNo: event.sequenceNo,
      eventType: event.eventType,
      latitude: event.latitude as number,
      longitude: event.longitude as number,
      accuracyMeters: event.accuracyMeters,
      occurredAtServer: event.occurredAtServer,
      distanceToDestinationMeters: event.distanceToDestinationMeters,
    });
  }

  // `sequenceNo` adalah urutan yang ditetapkan SERVER. Mengurutkan menurut waktu akan mengikuti
  // jam perangkat kurir, yang bisa salah setel — dan garisnya akan menyilang tanpa sebab.
  points.sort((a, b) => a.sequenceNo - b.sequenceNo);
  return { points, withoutLocation };
}

/**
 * Kotak yang memuat seluruh titik, plus tujuan bila ada.
 *
 * `null` bila tidak ada apa pun untuk dilihat — pemanggil yang memutuskan apa yang ditampilkan
 * sebagai gantinya. Tidak ada nilai bawaan yang dikarang di sini: peta yang memusat ke koordinat
 * karangan terlihat persis seperti peta yang bekerja.
 */
export function boundsOf(points: readonly LatLng[], destination?: LatLng | null): Bounds | null {
  const semua: LatLng[] = [...points];
  if (destination && usable(destination.latitude, destination.longitude)) {
    semua.push(destination);
  }
  const pertama = semua[0];
  if (pertama === undefined) return null;

  let south = pertama.latitude;
  let north = pertama.latitude;
  let west = pertama.longitude;
  let east = pertama.longitude;
  for (const titik of semua) {
    if (titik.latitude < south) south = titik.latitude;
    if (titik.latitude > north) north = titik.latitude;
    if (titik.longitude < west) west = titik.longitude;
    if (titik.longitude > east) east = titik.longitude;
  }
  return { south, west, north, east };
}

/** Tujuan tugas sebagai titik, atau `null` bila tugasnya memang tak bertujuan tetap. */
export function destinationOf(task: {
  readonly destinationLatitude: number | null;
  readonly destinationLongitude: number | null;
}): LatLng | null {
  if (!usable(task.destinationLatitude, task.destinationLongitude)) return null;
  return {
    latitude: task.destinationLatitude as number,
    longitude: task.destinationLongitude as number,
  };
}
